/**
 * As rotas **publicas** — lojas, catalogo, busca, zonas de entrega (`API §6.5`–`§6.7`,
 * `§6.10`).
 *
 * Nenhuma linha aqui redeclara forma de requisicao ou de resposta: o tipo de entrada e
 * `z.input<typeof XSchema>` e o de saida e o `z.infer` que `@farmacia/contracts` ja exporta.
 * `z.input` (e nao `z.infer`) na entrada por causa dos `.default()` e dos `z.coerce` do
 * contrato — quem chama nao deveria ser obrigado a passar `sort` e `limit` so porque o schema
 * lhes da default.
 *
 * ## Duas ausencias que sao contrato
 *
 * - **nenhum destes metodos devolve numero de estoque.** `AvailabilityResponseSchema` so tem
 *   `AvailabilityBand`, e o `ProductListItem` idem (RN-063, X-22). Nao ha o que filtrar aqui
 *   porque nao ha o que chegar.
 * - **nenhum aceita classe `CTRL_*`.** `PublicRegulatoryClassSchema` do contrato ja a exclui
 *   (REG-002).
 */
import {
  AvailabilityResponseSchema,
  CategoryTreeResponseSchema,
  DeliveryZonesResponseSchema,
  ProductDetailSchema,
  ProductListResponseSchema,
  SearchResponseSchema,
  StoreDetailSchema,
  StoreListResponseSchema,
  SuggestResponseSchema,
  type AvailabilityQuerySchema,
  type AvailabilityResponse,
  type CategoryTreeResponse,
  type DeliveryZonesResponse,
  type ProductDetail,
  type ProductListQuerySchema,
  type ProductListResponse,
  type SearchQuerySchema,
  type SearchResponse,
  type StoreDetail,
  type StoreListResponse,
  type SuggestQuerySchema,
  type SuggestResponse,
} from '@farmacia/contracts';
import type { z } from 'zod';

import type { ApiClientCore } from '../core.js';

export type ProductListInput = z.input<typeof ProductListQuerySchema>;
export type SearchInput = z.input<typeof SearchQuerySchema>;
export type SuggestInput = z.input<typeof SuggestQuerySchema>;
export type AvailabilityInput = Omit<z.input<typeof AvailabilityQuerySchema>, 'skus'> & {
  /** O contrato le `?skus=a,b,c`; aqui e uma lista, e a juncao e do `buildUrl`. */
  readonly skus: readonly string[];
};

export interface StoresApi {
  list: (signal?: AbortSignal) => Promise<StoreListResponse>;
  bySlug: (slug: string, signal?: AbortSignal) => Promise<StoreDetail>;
}

export interface CatalogApi {
  categories: (signal?: AbortSignal) => Promise<CategoryTreeResponse>;
  products: (query: ProductListInput, signal?: AbortSignal) => Promise<ProductListResponse>;
  product: (
    slug: string,
    query: { readonly storeId: string },
    signal?: AbortSignal,
  ) => Promise<ProductDetail>;
  availability: (query: AvailabilityInput, signal?: AbortSignal) => Promise<AvailabilityResponse>;
  search: (query: SearchInput, signal?: AbortSignal) => Promise<SearchResponse>;
  suggest: (query: SuggestInput, signal?: AbortSignal) => Promise<SuggestResponse>;
}

export interface DeliveryApi {
  zones: (query: { readonly storeId: string }, signal?: AbortSignal) => Promise<DeliveryZonesResponse>;
}

/** `signal` opcional em toda leitura: trocar de tela cancela a requisicao, e nao a ignora. */
function withSignal(signal: AbortSignal | undefined): { readonly signal?: AbortSignal } {
  return signal === undefined ? {} : { signal };
}

export function createStoresApi(core: ApiClientCore): StoresApi {
  return {
    async list(signal) {
      const { data } = await core.request({
        method: 'GET',
        path: '/v1/stores',
        response: StoreListResponseSchema,
        ...withSignal(signal),
      });
      return data;
    },

    async bySlug(slug, signal) {
      const { data } = await core.request({
        method: 'GET',
        path: `/v1/stores/${encodeURIComponent(slug)}`,
        response: StoreDetailSchema,
        ...withSignal(signal),
      });
      return data;
    },
  };
}

export function createCatalogApi(core: ApiClientCore): CatalogApi {
  return {
    async categories(signal) {
      const { data } = await core.request({
        method: 'GET',
        path: '/v1/catalog/categories',
        response: CategoryTreeResponseSchema,
        ...withSignal(signal),
      });
      return data;
    },

    async products(query, signal) {
      const { data } = await core.request({
        method: 'GET',
        path: '/v1/catalog/products',
        query: { ...query },
        response: ProductListResponseSchema,
        ...withSignal(signal),
      });
      return data;
    },

    async product(slug, query, signal) {
      const { data } = await core.request({
        method: 'GET',
        path: `/v1/catalog/products/${encodeURIComponent(slug)}`,
        query: { storeId: query.storeId },
        response: ProductDetailSchema,
        ...withSignal(signal),
      });
      return data;
    },

    async availability(query, signal) {
      const { data } = await core.request({
        method: 'GET',
        path: '/v1/catalog/availability',
        query: { storeId: query.storeId, skus: query.skus },
        response: AvailabilityResponseSchema,
        ...withSignal(signal),
      });
      return data;
    },

    async search(query, signal) {
      const { data } = await core.request({
        method: 'GET',
        path: '/v1/search',
        query: { ...query },
        response: SearchResponseSchema,
        ...withSignal(signal),
      });
      return data;
    },

    async suggest(query, signal) {
      const { data } = await core.request({
        method: 'GET',
        path: '/v1/search/suggest',
        query: { ...query },
        response: SuggestResponseSchema,
        ...withSignal(signal),
      });
      return data;
    },
  };
}

export function createDeliveryApi(core: ApiClientCore): DeliveryApi {
  return {
    async zones(query, signal) {
      const { data } = await core.request({
        method: 'GET',
        path: '/v1/delivery/zones',
        query: { storeId: query.storeId },
        response: DeliveryZonesResponseSchema,
        ...withSignal(signal),
      });
      return data;
    },
  };
}
